import {Button} from "@zeg/ui/Button";
import * as React from "react";
import {analytics} from "../services/analytics";

interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
}

interface ErrorBoundaryProps {
  children: React.ReactNode;
  fallback?: React.ComponentType<{error: Error; resetError: () => void}>;
  onReset?: () => void;
}

export class ErrorBoundary extends React.Component<
  ErrorBoundaryProps,
  ErrorBoundaryState
> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = {hasError: false, error: null};
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    // Update state so the next render will show the fallback UI
    return {hasError: true, error};
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    // Log the error to PostHog
    analytics.captureException(error, {
      componentStack: errorInfo.componentStack,
      errorBoundary: true,
      platform: typeof window !== "undefined" && window.api ? "desktop" : "web",
    });

    // Also log to console in development
    const isDev =
      typeof process !== "undefined"
        ? process.env.NODE_ENV === "development"
        : (typeof import.meta !== "undefined" &&
            (import.meta as any).env?.DEV) ||
          false;

    if (isDev) {
      console.error("Error caught by boundary:", error, errorInfo);
    }
  }

  resetError = () => {
    this.setState({hasError: false, error: null});
    // Call the onReset callback if provided
    this.props.onReset?.();
  };

  render() {
    if (this.state.hasError && this.state.error) {
      // Use custom fallback if provided
      if (this.props.fallback) {
        const FallbackComponent = this.props.fallback;
        return (
          <FallbackComponent
            error={this.state.error}
            resetError={this.resetError}
          />
        );
      }

      // Default fallback UI
      return (
        <div className="flex min-h-[400px] flex-col items-center justify-center gap-4 p-8">
          <div className="text-center">
            <h2 className="mb-2 text-lg font-medium">Something went wrong</h2>
            <p className="text-muted-foreground text-sm">
              An unexpected error occurred. Please try again.
            </p>
            {((typeof process !== "undefined" &&
              process.env.NODE_ENV === "development") ||
              (typeof import.meta !== "undefined" &&
                (import.meta as any).env?.DEV)) && (
              <pre className="bg-muted mt-4 max-w-md overflow-auto rounded p-2 font-mono text-xs">
                {this.state.error.message}
              </pre>
            )}
          </div>
          <Button onClick={this.resetError}>Try Again</Button>
        </div>
      );
    }

    return this.props.children;
  }
}

// Hook version for functional components
export function useErrorHandler() {
  return React.useCallback((error: Error, errorInfo?: any) => {
    analytics.captureException(error, {
      ...errorInfo,
      platform: typeof window !== "undefined" && window.api ? "desktop" : "web",
    });
  }, []);
}
